import React, { useState, useEffect } from 'react';
import { useVoice } from '../VoiceContext';
import {
  getAllLanguages,
  getThemesByLanguage,
  getFiltersByUser,
  getWordsByTheme,
  getWordsByFilter,
  getWordsByThemeAndFilter,
  createFilter,
  createTheme,
  createWord,
  deleteWord,
  deleteFilter,
  deleteTheme
} from '../api';
import {  
  Box, Typography, Paper, CircularProgress, Alert, Chip, Stack,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip,
  Card, CardContent, useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useTheme } from '@mui/material/styles';

const WordsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [filters, setFilters] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [words, setWords] = useState([]);
  const [activeSpeech, setActiveSpeech] = useState(null);
  const { voices: availableVoices, loading: voicesLoading } = useVoice();




  const [loading, setLoading] = useState({
    languages: false,
    themes: false,
    filters: false,
    words: false
  });

  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Модальные окна
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');

  const [openThemeDialog, setOpenThemeDialog] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');

  const [openWordDialog, setOpenWordDialog] = useState(false);
  const [russianWord, setRussianWord] = useState('');
  const [foreignWord, setForeignWord] = useState('');
  const [newWordTheme, setNewWordTheme] = useState(null);
  const [newWordFilter, setNewWordFilter] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null,
    payload: null
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(prev => ({ ...prev, languages: true, filters: true }));
        const [langs, userFilters] = await Promise.all([
          getAllLanguages(),
          getFiltersByUser()
        ]);
        setLanguages(langs);
        setFilters(userFilters);
        if (langs.length > 0) setSelectedLanguage(langs[0].id);
      } catch (err) {
        setError('Failed to load initial data');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, languages: false, filters: false }));
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    const loadThemes = async () => {
      if (!selectedLanguage) return;
      try {
        setLoading(prev => ({ ...prev, themes: true }));
        const themeData = await getThemesByLanguage(selectedLanguage);
        themeData.sort((a, b) => {
          const numA = parseInt(a.name.match(/^\d+/)?.[0] || 0, 10);
          const numB = parseInt(b.name.match(/^\d+/)?.[0] || 0, 10);
          if (numA !== numB) return numA - numB;
          return a.name.localeCompare(b.name);
        });
        setThemes(themeData);
        setSelectedTheme(null);
        setWords([]);
      } catch (err) {
        setError('Failed to load themes');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, themes: false }));
      }
    };

    loadThemes();
  }, [selectedLanguage]);

  useEffect(() => {
    const loadWords = async () => {
      if (!selectedTheme && !selectedFilter) {
        setWords([]);
        return;
      }

      try {
        setLoading(prev => ({ ...prev, words: true }));
        let data = [];

        if (selectedTheme && selectedFilter) {
          data = await getWordsByThemeAndFilter(selectedTheme, selectedFilter);
        } else if (selectedTheme) {
          data = await getWordsByTheme(selectedTheme);
        } else if (selectedFilter) {
          data = await getWordsByFilter(selectedFilter);
        }

        setWords(data);
        setCurrentPage(1);
      } catch (err) {
        setError('Failed to load words');
        console.error(err);
      } finally {
        setLoading(prev => ({ ...prev, words: false }));
      }
    };

    loadWords();
  }, [selectedTheme, selectedFilter]);

  const clearFilter = () => setSelectedFilter(null);
  const clearTheme = () => setSelectedTheme(null);
  const selectedThemeData = themes.find(theme => theme.id === selectedTheme);
  const selectedFilterData = filters.find(filter => filter.id === selectedFilter);
  const canDeleteSelectedTheme = Boolean(selectedThemeData?.user_id);
  const canDeleteSelectedFilter = Boolean(selectedFilterData?.user_id);

  const speakWord = (word) => {
    if (activeSpeech) {
      window.speechSynthesis.cancel();
    }

    if (voicesLoading || availableVoices.length === 0) {
      console.warn('Speech synthesis: No voices available or still loading. Cannot speak.');
      return;
    }

    const utterance = new SpeechSynthesisUtterance(word);
    const voiceName = localStorage.getItem('defaultItalianVoice');
    const selectedVoice = voiceName
      ? availableVoices.find(v => v.name === voiceName)
      : null;

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    } else {
      console.warn('Selected voice not found, using default system voice');
    }

    setActiveSpeech(utterance);
    utterance.onend = () => setActiveSpeech(null);
    window.speechSynthesis.speak(utterance);
  };

  const handleCreateFilter = async () => {
    if (!newFilterName.trim()) return;

    try {
      const newFilter = await createFilter(newFilterName.trim());
      setFilters(prev => [...prev, newFilter]);
      setSelectedFilter(newFilter.id);
      setNewFilterName('');
      setOpenFilterDialog(false);
    } catch (err) {
      setError('Не удалось создать фильтр');
      console.error(err);
    }
  };

  const handleCreateTheme = async () => {
    if (!newThemeName.trim() || !selectedLanguage) return;

    try {
      const newTheme = await createTheme({
        name: newThemeName.trim(),
        language_id: selectedLanguage
      });
      setThemes(prev => [...prev, newTheme]);
      setSelectedTheme(newTheme.id);
      setNewThemeName('');
      setOpenThemeDialog(false);
    } catch (err) {
      setError('Не удалось создать тему');
      console.error(err);
    }
  };

  const handleCreateWord = async () => {
    if (!russianWord.trim() || !foreignWord.trim() || !selectedLanguage || !newWordTheme) return;

    try {
      const newWord = await createWord({
        russian_word: russianWord.trim(),
        foreign_word: foreignWord.trim(),
        language_id: selectedLanguage,
        theme_id: newWordTheme,
        filter_id: newWordFilter || null
      });

      setWords(prev => [...prev, newWord]);
      setRussianWord('');
      setForeignWord('');
      setNewWordTheme(null);
      setNewWordFilter(null);
      setOpenWordDialog(false);
    } catch (err) {
      setError('Не удалось создать слово');
      console.error(err);
    }
  };

  const handleDeleteTheme = async () => {
    if (!selectedThemeData || !canDeleteSelectedTheme) return;
    setConfirmDialog({
      open: true,
      type: 'theme',
      payload: selectedThemeData
    });
  };

  const handleDeleteFilter = async () => {
    if (!selectedFilterData || !canDeleteSelectedFilter) return;
    setConfirmDialog({
      open: true,
      type: 'filter',
      payload: selectedFilterData
    });
  };

  const handleDeleteWord = async (word) => {
    if (!word?.user_id) return;
    setConfirmDialog({
      open: true,
      type: 'word',
      payload: word
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ open: false, type: null, payload: null });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDialog.payload || !confirmDialog.type) return;

    try {
      if (confirmDialog.type === 'theme') {
        const theme = confirmDialog.payload;
        await deleteTheme(theme.id);
        setThemes(prev => prev.filter(item => item.id !== theme.id));
        if (selectedTheme === theme.id) {
          setSelectedTheme(null);
          setWords([]);
        }
      }

      if (confirmDialog.type === 'word') {
        const word = confirmDialog.payload;
        await deleteWord(word.id);
        setWords(prev => prev.filter(item => item.id !== word.id));
      }

      if (confirmDialog.type === 'filter') {
        const filter = confirmDialog.payload;
        await deleteFilter(filter.id);
        setFilters(prev => prev.filter(item => item.id !== filter.id));
        if (selectedFilter === filter.id) {
          setSelectedFilter(null);
        }
      }
    } catch (err) {
      if (confirmDialog.type === 'theme') {
        setError('Не удалось удалить тему');
      } else if (confirmDialog.type === 'word') {
        setError('Не удалось удалить слово');
      } else {
        setError('Не удалось удалить фильтр');
      }
      console.error(err);
    } finally {
      handleCloseConfirmDialog();
    }
  };

  const confirmDialogTitle = confirmDialog.type === 'theme'
    ? 'Удалить тему?'
    : confirmDialog.type === 'word'
      ? 'Удалить слово?'
      : 'Удалить фильтр?';

  const confirmDialogText = confirmDialog.type === 'theme'
    ? `Тема "${confirmDialog.payload?.name}" будет удалена вместе со всеми словами в этой теме.`
    : confirmDialog.type === 'word'
      ? `Слово "${confirmDialog.payload?.foreign_word}" будет удалено без возможности восстановления.`
      : `Фильтр "${confirmDialog.payload?.filter_name}" будет удален.`;


  const paginatedWords = words.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(words.length / itemsPerPage);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" gutterBottom>
        Словарь
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="filters-panel-content"
            id="filters-panel-header"
          >
            <Typography variant="h6" gutterBottom>Фильтры</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              <FormControl sx={{ minWidth: 200, flex: 1 }} disabled={loading.languages}>
                <InputLabel>Язык</InputLabel>
                <Select
                  value={selectedLanguage || ''}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  label="Язык"
                >
                  {languages.map(lang => (
                    <MenuItem key={lang.id} value={lang.id}>{lang.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedLanguage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                  <FormControl sx={{ minWidth: { xs: 0, md: 200 }, flex: 1 }} disabled={loading.themes}>
                    <InputLabel>Тема</InputLabel>
                    <Select
                      value={selectedTheme || ''}
                      onChange={(e) => setSelectedTheme(e.target.value)}
                      label="Тема"
                    >
                      {themes.map(theme => (
                        <MenuItem key={theme.id} value={theme.id}>{theme.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Добавить тему">
                    <IconButton color="primary" onClick={() => setOpenThemeDialog(true)} aria-label="Добавить тему">
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Удалить тему">
                    <span>
                      <IconButton
                        color="error"
                        onClick={handleDeleteTheme}
                        disabled={!canDeleteSelectedTheme}
                        aria-label="Удалить тему"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                <FormControl sx={{ minWidth: { xs: 0, md: 200 }, flex: 1 }} disabled={loading.filters}>
                  <InputLabel>Доп. фильтры</InputLabel>
                  <Select
                    value={selectedFilter || ''}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    label="Мои фильтры"
                  >
                    <MenuItem value="">
                      <em>Нет</em>
                    </MenuItem>
                    {filters.map(filter => (
                      <MenuItem key={filter.id} value={filter.id}>{filter.filter_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Добавить фильтр">
                  <IconButton color="primary" onClick={() => setOpenFilterDialog(true)} aria-label="Добавить фильтр">
                    <AddIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Удалить фильтр">
                  <span>
                    <IconButton
                      color="error"
                      onClick={handleDeleteFilter}
                      disabled={!canDeleteSelectedFilter}
                      aria-label="Удалить фильтр"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Box>
          <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Слова</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {loading.words && <CircularProgress size={24} />}
              <Button variant="contained" onClick={() => setOpenWordDialog(true)}>
                + слово
              </Button>
            </Box>
          </Box>


            <Stack direction="row" sx={{ mb: 2, gap: 1 }} flexWrap="wrap">
              {selectedTheme && (
                <Chip
                  label={`Тема: ${selectedThemeData?.name || selectedTheme}`}
                  onDelete={clearTheme}
                />
              )}
              {selectedFilter && (
                <Chip
                  label={`Фильтр: ${selectedFilterData?.filter_name || selectedFilter}`}
                  onDelete={clearFilter}
                />
              )}
            </Stack>

            {words.length > 0 ? (
              <>
                {isMobile ? (
                  <Stack spacing={1.5}>
                    {paginatedWords.map((word) => (
                      <Card key={word.id} variant="outlined">
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack spacing={1.25}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {word.foreign_word}
                              </Typography>
                              <IconButton
                                color="primary"
                                onClick={() => speakWord(word.foreign_word)}
                                disabled={activeSpeech?.text === word.foreign_word}
                                aria-label={`Произнести слово ${word.foreign_word}`}
                                sx={{ width: 44, height: 44 }}
                              >
                                <VolumeUpIcon />
                              </IconButton>
                            </Stack>
                            <Typography variant="body1" sx={{ fontSize: '1.05rem' }}>
                              {word.russian_word}
                            </Typography>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteWord(word)}
                              disabled={!word.user_id}
                              aria-label={`Удалить слово ${word.foreign_word}`}
                              sx={{ width: 44, height: 44, alignSelf: 'flex-end' }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Русский</TableCell>
                          <TableCell>Иностранный</TableCell>
                          <TableCell>Произношение</TableCell>
                          <TableCell align="center" sx={{ width: 64 }}>Действие</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedWords.map((word) => (
                          <TableRow key={word.id}>
                            <TableCell>{word.russian_word}</TableCell>
                            <TableCell>{word.foreign_word}</TableCell>
                            <TableCell>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => speakWord(word.foreign_word)}
                                disabled={activeSpeech?.text === word.foreign_word}
                                aria-label={`Произнести слово ${word.foreign_word}`}
                              >
                                <VolumeUpIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteWord(word)}
                                disabled={!word.user_id}
                                aria-label={`Удалить слово ${word.foreign_word}`}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {isMobile ? (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 1.25,
                      pb: 1.25,
                      px: 0.5,
                      backgroundColor: 'background.paper',
                      borderTop: 1,
                      borderColor: 'divider'
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                      <Button
                        variant="outlined"
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage <= 1}
                        sx={{ minHeight: 46, flex: 1, fontSize: '0.95rem' }}
                      >
                        Назад
                      </Button>
                      <Chip
                        label={`${currentPage} / ${totalPages}`}
                        color="primary"
                        variant="outlined"
                        sx={{ minWidth: 84, height: 40, fontWeight: 700 }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage >= totalPages}
                        sx={{ minHeight: 46, flex: 1, fontSize: '0.95rem' }}
                      >
                        Далее
                      </Button>
                    </Stack>
                  </Box>
                ) : (
                  <Stack alignItems="center" sx={{ mt: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(event, value) => setCurrentPage(value)}
                      color="primary"
                    />
                  </Stack>
                )}
              </>
            ) : (
              !loading.words && (
                <Typography variant="body1" color="text.secondary">
                  Слова не найдены. Выберите тему или фильтр для просмотра слов.
                </Typography>
              )
            )}
          </Paper>
        </Box>
      </Box>

      {/* Диалог создания фильтра */}
      <Dialog open={openFilterDialog} onClose={() => setOpenFilterDialog(false)}>
        <DialogTitle>Новый фильтр</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название фильтра"
            fullWidth
            value={newFilterName}
            onChange={(e) => setNewFilterName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFilterDialog(false)}>Отмена</Button>
          <Button onClick={handleCreateFilter} variant="contained">Создать</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог создания темы */}
      <Dialog open={openThemeDialog} onClose={() => setOpenThemeDialog(false)}>
        <DialogTitle>Новая тема</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Название темы"
            fullWidth
            value={newThemeName}
            onChange={(e) => setNewThemeName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenThemeDialog(false)}>Отмена</Button>
          <Button onClick={handleCreateTheme} variant="contained">Создать</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openWordDialog} onClose={() => setOpenWordDialog(false)} fullWidth>
        <DialogTitle>Добавить слово</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Русское слово"
            value={russianWord}
            onChange={(e) => setRussianWord(e.target.value)}
            fullWidth
          />
          <TextField
            label="Иностранное слово"
            value={foreignWord}
            onChange={(e) => setForeignWord(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Тема</InputLabel>
            <Select
              value={newWordTheme || ''}
              onChange={(e) => setNewWordTheme(e.target.value)}
              label="Тема"
            >
              {themes.map(theme => (
                <MenuItem key={theme.id} value={theme.id}>{theme.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Фильтр (необязательно)</InputLabel>
            <Select
              value={newWordFilter || ''}
              onChange={(e) => setNewWordFilter(e.target.value)}
              label="Фильтр"
            >
              <MenuItem value="">
                <em>Нет</em>
              </MenuItem>
              {filters.map(filter => (
                <MenuItem key={filter.id} value={filter.id}>{filter.filter_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWordDialog(false)}>Отмена</Button>
          <Button onClick={handleCreateWord} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={handleCloseConfirmDialog} fullWidth maxWidth="xs">
        <DialogTitle>{confirmDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{confirmDialogText}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Отмена</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default WordsPage;
